import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Power, ShieldAlert } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DangerDialog } from '@/components/DangerDialog'
import { fetchFeatureFlags, setFeatureFlag, type AdminFeatureFlag } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'

/**
 * A14.06 — Kill-switch. FAOL feature-flaglarни favqulodda darhol o'chirish. Har
 * «kill» — L3 DangerDialog (kalitni AYNAN yozish + sabab) → to'g'ridan `POST
 * /admin/settings/flags {enabled:false}` (four-eyes EMAS — favqulodda tez). Yozuv
 * audit'ga tushadi. AUDIT_VIEW-gated (yozuvni backend admin-gate qiladi).
 */
export function FeatureKillSwitch() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null)
  const [target, setTarget] = useState<AdminFeatureFlag | null>(null)

  const load = useCallback(() => {
    fetchFeatureFlags({ filters: [], sort: [], page: 1, pageSize: 500, search: '', signal: new AbortController().signal })
      .then((r) => setFlags(r.rows))
      .catch(() => setFlags([]))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const enabled = useMemo(() => (flags ?? []).filter((f) => f.enabled), [flags])

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={t.flags.killTitle}
        screenId="A14.06"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <p className="border-b border-border-subtle px-3 py-2 text-xs text-tertiary">{t.flags.killNote}</p>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {flags === null ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : enabled.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-tertiary">
            <ShieldAlert size={24} className="mx-auto mb-2 opacity-60" />
            {t.flags.killEmpty}
          </div>
        ) : (
          <div className="max-w-2xl space-y-2">
            {enabled.map((f) => (
              <div
                key={f.key}
                className="flex items-center justify-between rounded-lg border border-border-default bg-surface-1 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-sm text-primary" title={f.key}>{f.key}</div>
                  <div className="mt-0.5 text-xs text-tertiary">{f.value}</div>
                </div>
                <button
                  onClick={() => setTarget(f)}
                  className="ml-3 inline-flex shrink-0 items-center gap-1.5 rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                >
                  <Power size={14} />
                  {t.flags.kill}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <DangerDialog
        open={target !== null}
        onClose={() => setTarget(null)}
        screenId="A14.06"
        level={3}
        title={t.flags.killConfirmTitle}
        description={target ? t.flags.killConfirmDesc(target.key) : ''}
        confirmLabel={t.flags.kill}
        objectName={target?.key}
        onConfirm={async () => {
          if (!target) return
          try {
            await setFeatureFlag(target.key, { enabled: false })
            setFlags((fs) => fs?.map((x) => (x.key === target.key ? { ...x, enabled: false, value: 'false' } : x)) ?? null)
            toast(t.flags.killed, { tone: 'success' })
          } catch (e) {
            toast(t.flags.toggleFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
            throw e
          } finally {
            setTarget(null)
          }
        }}
      />
    </>
  )
}
